'use client';

import { getCanonicalPath } from '@/lib/algolia/getCanonicalPath';
import { ProductPageJsonLd } from './components/JsonLd';
import { ProductPageData } from './getData';
import Debug from '@/components/debug';
import ProductGallery from './components/ProductGallery';
import {
  ChevronRight,
  CopyIcon,
  ModelIcon,
  ProductTags,
  Promotion,
  RulerIcon,
  TableIcon,
} from '@fagmobler/ui';
import { formatServerTags } from '@/lib/algolia/tags';
import Link from 'next/link';
import { fagmoblerSlugify } from '@/lib/utils/getUrls';
import ProductPrice from './components/ProductPrice';
import ProductCollection from '@/cataloguePages/productPageWithVariants/components/ProductCollection';
import Divider from '@/cataloguePages/productPageWithVariants/components/Divider';
import { useMediaQuery } from 'usehooks-ts';
import SectionContainer from '@/cataloguePages/productPageWithVariants/components/SectionContainer';
import SectionContainerButton from '@/cataloguePages/productPageWithVariants/components/SectionContainerButton';
import SectionContainerStoreButton from '@/cataloguePages/productPageWithVariants/components/SectionContainerStoreButton';
import AddToCartButton from '@/cataloguePages/productPageWithVariants/components/AddToCartButton';
import ClickCollectButton from '@/cataloguePages/productPageWithVariants/components/ClickCollectButton';
import SectionHeader from '@/cataloguePages/productPageWithVariants/components/SectionHeader';
import { useStores } from '@/components/store';
import Variants from '@/cataloguePages/productPageWithVariants/components/Variants';
import Properties, {
  PropertiesModalKey,
} from '@/cataloguePages/productPageWithVariants/modals/Properties';
import { useState } from 'react';
import AboutSeries, {
  AboutSeriesModalKey,
} from '@/cataloguePages/productPageWithVariants/modals/AboutSeries';
import ProductDetails, {
  ProductDetailsModalKey,
} from '@/cataloguePages/productPageWithVariants/modals/ProductDetails';
import External3DLink, {
  External3DLinkModalKey,
} from '@/cataloguePages/productPageWithVariants/modals/External3DLink';
import MoreInfo, {
  MoreInfoModalKey,
} from '@/cataloguePages/productPageWithVariants/modals/MoreInfo';
import { ViewTracker } from '@/cataloguePages/productPageWithVariants/components/ViewTracker';
import { PrintMode } from '@/cataloguePages/productPageWithVariants/components/PrintMode';
import { FagmoblerAlgoliaProduct } from '@fagmobler/types';
import { PageHeader } from '@/components/layout/PageHeader';
import { ProductIcons } from '@/cataloguePages/productPageWithVariants/components/ProductIcons';
import { StoreAvailability } from '@/cataloguePages/productPageWithVariants/components/StoreAvailability';
import { ProductWarning } from '@/cataloguePages/productPageWithVariants/components/ProductWarning';
import StorePicker from '@/cataloguePages/productPageWithVariants/modals/StorePicker';

type CommonProps = {
  preview: boolean;
  previewData: any | null;
  slugs: string[] | null;
};

export type ProductPageProps = ProductPageData & CommonProps;

export function ProductPageWithVariants({
  product,
  omniumProduct,
  algoliaProduct,
  motherProductSKU,
  breadcrumbs,
  productEnrichment,
  serieEnrichment,
  relatedProducts,
  serieProducts,
  previewData,
  preview = false,
}: ProductPageProps) {
  const isMd = useMediaQuery('(min-width: 768px)');
  const isLg = useMediaQuery('(min-width: 1024px)');
  const { current: currentStoreQuery } = useStores();
  const [openedModal, setOpenedModal] = useState<string | null>(null);

  if (!product || !algoliaProduct || !omniumProduct) return null;

  const canonicalPath = getCanonicalPath(algoliaProduct);
  const canonicalUrl =
    canonicalPath && `${process.env.NEXT_PUBLIC_SITE_URL}${canonicalPath}`;
  const tagKeys = product.tags?.map((tag) => tag.key) || [];
  const serie = product.properties?.find((p) => p.key === 'serie');
  const currentStore = currentStoreQuery.data?.store;

  const openModal = (modal: string) => {
    setOpenedModal(modal);
  };

  const closeModal = () => {
    setOpenedModal(null);
  };

  const hasValidSerie =
    !!serie &&
    !!serieEnrichment &&
    !!serieEnrichment.body &&
    serieEnrichment.body.length > 0;

  const seriesHits: FagmoblerAlgoliaProduct[] = (
    serieProducts?.results?.[0]?.hits ?? []
  )
    .filter(
      (hit: FagmoblerAlgoliaProduct) =>
        hit.objectID !== product.sku && hit.objectID !== motherProductSKU
    )
    .slice(0, 4);
  const relatedHits: FagmoblerAlgoliaProduct[] =
    relatedProducts?.results?.[0]?.hits ?? [];

  const currentStoreStock = product.stock?.find(
    (s) => s.storeId === currentStore?.id
  );

  return (
    <>
      <PageHeader breadcrumbs={breadcrumbs} />
      <ProductPageJsonLd
        algoliaProduct={algoliaProduct}
        product={product}
        canonicalUrl={canonicalUrl}
      />
      <ViewTracker product={product} />

      <PrintMode product={product} />
      <div className="md:grid md:grid-cols-12 gap-8 mt-5 md:mt-10 print:!hidden">
        <ProductGallery product={product}>
          {product.price?.discountPercent &&
            product.price?.discountPercent > 0 && (
              <Promotion
                size={isLg ? 'md' : 'base'}
                className="absolute bottom-4 right-4 z-10"
                percent={product.price?.discountPercent}
                innerClassName={
                  tagKeys.includes('BLACK WEEK')
                    ? 'bg-[#F4A933] text-neutral-80'
                    : ''
                }
              />
            )}
        </ProductGallery>

        <div className="mt-5 md:mt-0 md:col-span-5 lg:col-span-6 flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            {product.tags && (
              <ProductTags
                tags={formatServerTags(product.tags, 4)}
                hasMargins={false}
                variant="pdp_new"
                size={isMd ? 'md' : 'sm'}
              />
            )}

            <div className="flex justify-between items-center gap-2">
              <div>
                <h1 className="hd-lg print:hd-3xl print:mt-0">
                  {product.posterName}
                </h1>
                {product.posterDescription && (
                  <span className="block text-neutral-70 body-sm mb-4">
                    {product.posterDescription}
                  </span>
                )}
                {serie && (
                  <span className="cap-sm font-bold">
                    Serie:{' '}
                    <Link
                      href={`/serie/${fagmoblerSlugify(serie.value as string)}`}
                      className="cap-link-sm font-bold underline hover:no-underline"
                    >
                      Se {serie.value}-serien
                    </Link>
                  </span>
                )}
              </div>
              <ProductPrice price={product.price} size={isMd ? 'lg' : 'md'} />
            </div>
          </div>

          <Divider />

          {product.texts && (
            <p className="body-sm opacity-90">
              {product.texts.shortdescription &&
                product.texts.shortdescription
                  .split(/(?<=[.!?])\s+/)
                  .slice(0, 2)
                  .join(' ')}
            </p>
          )}

          {!!product.texts?.longdescription?.trim() && (
            <button
              onClick={() => {
                openModal(MoreInfoModalKey);
              }}
              className="cap-link-sm flex gap-0 font-bold items-center no-underline hover:underline"
            >
              Les mer om produktet <ChevronRight className="size-4" />
            </button>
          )}

          {product.icons && <ProductIcons icons={product.icons} />}

          <Divider />

          <Variants
            product={product}
            currentStoreStock={currentStoreStock?.stock}
            currentStoreStockText={currentStoreStock?.reasonText}
          />

          {product.external3DLink && (
            <SectionContainer>
              <SectionContainerButton
                title="Bygg i 3D"
                Icon={ModelIcon}
                className="py-0"
                onClick={() => {
                  openModal(External3DLinkModalKey);
                }}
              />
            </SectionContainer>
          )}

          <ProductWarning product={product} />

          <div>
            <SectionHeader title="Velg butikk:" />
            <SectionContainer>
              <SectionContainerStoreButton
                omniumProduct={omniumProduct}
                algoliaProduct={algoliaProduct}
                product={product}
                openModal={openModal}
              />
              <StoreAvailability product={product} openModal={openModal} />
            </SectionContainer>
          </div>

          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <AddToCartButton
              product={product}
              algoliaProduct={algoliaProduct}
            />
            <ClickCollectButton
              product={product}
              omniumProduct={omniumProduct}
            />
          </div>

          <SectionContainer>
            <SectionContainerButton
              title="Produktdetalj"
              Icon={TableIcon}
              onClick={() => {
                openModal(ProductDetailsModalKey);
              }}
            />
            <SectionContainerButton
              title="Mål"
              Icon={RulerIcon}
              onClick={() => {
                openModal(PropertiesModalKey);
              }}
            />
            {hasValidSerie && (
              <SectionContainerButton
                title={`${serie.value}-serien`}
                Icon={CopyIcon}
                onClick={() => {
                  openModal(AboutSeriesModalKey);
                }}
              />
            )}
          </SectionContainer>
        </div>

        {relatedHits && relatedHits.length > 2 && (
          <div className="md:col-span-12 mt-8">
            <ProductCollection
              title="Andre så også på:"
              products={relatedHits}
            />
          </div>
        )}
        {serie && seriesHits && seriesHits.length > 2 && (
          <div className="md:col-span-12 mt-8">
            <ProductCollection
              products={seriesHits}
              title={`Mer fra ${serie.value.toString().toLowerCase()} serien:`}
            />
          </div>
        )}
      </div>

      <MoreInfo
        product={product}
        openedModal={openedModal}
        closeModal={closeModal}
      />
      <Properties
        product={product}
        openedModal={openedModal}
        closeModal={closeModal}
        filterGroup="measurment"
      />
      {hasValidSerie && (
        <AboutSeries
          title={serie.value as string}
          serieEnrichment={serieEnrichment}
          openedModal={openedModal}
          closeModal={closeModal}
        />
      )}
      <ProductDetails
        product={product}
        productEnrichment={productEnrichment}
        openedModal={openedModal}
        closeModal={closeModal}
      />
      <External3DLink
        product={product}
        openedModal={openedModal}
        closeModal={closeModal}
      />
      <StorePicker
        product={product}
        openedModal={openedModal}
        closeModal={closeModal}
      />

      <div className="print:hidden">
        <Debug data={product} label="Product" />
        <Debug data={algoliaProduct} label="algoliaProduct" />
      </div>
    </>
  );
}

export default ProductPageWithVariants;
